import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Select, Statistic, Row, Col, message, Popconfirm, Typography } from 'antd';
import { ReloadOutlined, SyncOutlined, DatabaseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { adminReconciliationEndpoints, ReconciliationMismatch, ReconciliationReport } from '../api/adminTreasuryEndpoints';
import { formatCurrency } from '../utils/formatters';

const { Text } = Typography;

// Mismatch type labels and colors
const MISMATCH_TYPE_CONFIG = {
  pending_too_long: { label: 'Pending Too Long', color: 'orange' },
  missing_ledger_entry: { label: 'Missing Ledger Entry', color: 'red' },
  status_mismatch: { label: 'Status Mismatch', color: 'purple' },
  orphan_ledger_entry: { label: 'Orphan Ledger Entry', color: 'magenta' },
};

const AdminReconciliation: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [windowDays, setWindowDays] = useState(30);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await adminReconciliationEndpoints.getReport({
        window_days: windowDays,
        type: filterType,
      });
      setReport(response.data);
    } catch (error) {
      message.error('Failed to load reconciliation report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [windowDays, filterType]);

  const handleRefreshStatus = async (transferId: string) => {
    const key = `refresh-${transferId}`;
    setActionLoading({ ...actionLoading, [key]: true });
    try {
      await adminReconciliationEndpoints.refreshStatus(transferId);
      message.success('Status refreshed successfully');
      fetchReport(); // Reload report
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to refresh status');
    } finally {
      setActionLoading({ ...actionLoading, [key]: false });
    }
  };

  const handleApplyLedgerMapping = async (transferId: string) => {
    const key = `ledger-${transferId}`;
    setActionLoading({ ...actionLoading, [key]: true });
    try {
      const response = await adminReconciliationEndpoints.applyLedgerMapping(transferId);
      message.success(response.data.message);
      fetchReport(); // Reload report
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to apply ledger mapping');
    } finally {
      setActionLoading({ ...actionLoading, [key]: false });
    }
  };

  const renderActions = (record: ReconciliationMismatch) => {
    return (
      <Space size="small">
        {record.suggested_actions.includes('refresh_status') && (
          <Button
            size="small"
            icon={<SyncOutlined />}
            loading={actionLoading[`refresh-${record.transfer_id}`]}
            onClick={() => handleRefreshStatus(record.transfer_id)}
          >
            Refresh
          </Button>
        )}
        {record.suggested_actions.includes('apply_ledger_mapping') && (
          <Popconfirm
            title="Apply ledger mapping?"
            description="This will create a company ledger entry for this completed transfer."
            onConfirm={() => handleApplyLedgerMapping(record.transfer_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              icon={<DatabaseOutlined />}
              loading={actionLoading[`ledger-${record.transfer_id}`]}
              type="primary"
            >
              Apply Ledger
            </Button>
          </Popconfirm>
        )}
        {record.suggested_actions.includes('contact_support') && (
          <Tag color="orange" icon={<ExclamationCircleOutlined />}>
            Contact Support
          </Tag>
        )}
        {record.suggested_actions.includes('flag_for_review') && (
          <Tag color="red" icon={<ExclamationCircleOutlined />}>
            Needs Review
          </Tag>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'mismatch_type',
      key: 'mismatch_type',
      width: 180,
      render: (type: string) => {
        const config = MISMATCH_TYPE_CONFIG[type as keyof typeof MISMATCH_TYPE_CONFIG];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: Object.entries(MISMATCH_TYPE_CONFIG).map(([value, config]) => ({
        text: config.label,
        value,
      })),
      onFilter: (value: any, record: ReconciliationMismatch) => record.mismatch_type === value,
    },
    {
      title: 'Transfer',
      key: 'transfer',
      width: 200,
      render: (record: ReconciliationMismatch) => (
        <div>
          <div><Text copyable={{ text: record.transfer_id }}>{record.transfer_id.substring(0, 12)}...</Text></div>
          {record.transfer_type && <Text type="secondary" style={{ fontSize: 12 }}>{record.transfer_type}</Text>}
        </div>
      ),
    },
    {
      title: 'Owner',
      key: 'owner',
      width: 200,
      render: (record: ReconciliationMismatch) => (
        record.owner_name ? (
          <div>
            <div>{record.owner_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.owner_email}</Text>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount_cents',
      width: 120,
      align: 'right' as const,
      render: (cents: number) => formatCurrency(cents),
    },
    {
      title: 'Internal Status',
      dataIndex: 'internal_status',
      key: 'internal_status',
      width: 120,
      render: (status: string) => <Tag>{status}</Tag>,
    },
    {
      title: 'Provider Status',
      dataIndex: 'provider_status',
      key: 'provider_status',
      width: 120,
      render: (status?: string) => status ? <Tag color="blue">{status}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Age',
      dataIndex: 'age_days',
      key: 'age_days',
      width: 80,
      align: 'right' as const,
      render: (days: number) => `${days}d`,
      sorter: (a: ReconciliationMismatch, b: ReconciliationMismatch) => a.age_days - b.age_days,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (record: ReconciliationMismatch) => renderActions(record),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Reconciliation</h1>
        <Space>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
            placeholder="All Types"
            allowClear
          >
            {Object.entries(MISMATCH_TYPE_CONFIG).map(([value, config]) => (
              <Select.Option key={value} value={value}>
                {config.label}
              </Select.Option>
            ))}
          </Select>
          <Select
            value={windowDays}
            onChange={setWindowDays}
            style={{ width: 150 }}
          >
            <Select.Option value={7}>Last 7 days</Select.Option>
            <Select.Option value={14}>Last 14 days</Select.Option>
            <Select.Option value={30}>Last 30 days</Select.Option>
            <Select.Option value={60}>Last 60 days</Select.Option>
            <Select.Option value={90}>Last 90 days</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {report && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Mismatches"
                  value={report.summary.total_count}
                  valueStyle={{ color: report.summary.total_count > 0 ? '#cf1322' : '#3f8600' }}
                />
              </Card>
            </Col>
            {Object.entries(report.summary.by_type).map(([type, count]) => {
              const config = MISMATCH_TYPE_CONFIG[type as keyof typeof MISMATCH_TYPE_CONFIG];
              return (
                <Col span={6} key={type}>
                  <Card>
                    <Statistic
                      title={config.label}
                      value={count}
                      valueStyle={{ color: count > 0 ? '#faad14' : '#3f8600' }}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Card>
            <Table
              columns={columns}
              dataSource={report.mismatches}
              rowKey="transfer_id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} mismatches`,
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminReconciliation;
